import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BeforeAfter } from "@/components/BeforeAfter";
import { ConsultButton } from "@/components/CtaButtons";
import { FinalCta } from "@/sections/HomeVoice";
import { ProjectCard } from "@/components/ProjectCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

const InfoRow = ({ label, value }) =>
  value ? (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-charcoal/45">{label}</dt>
      <dd className="mt-1.5 font-display text-xl text-charcoal">{value}</dd>
    </div>
  ) : null;

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api.get(`/projects/${slug}`)
      .then((r) => {
        setProject(r.data);
        setStatus("ok");
        api.get("/projects").then((all) => {
          setRelated(all.data.filter((p) => p.slug !== slug && p.category === r.data.category).slice(0, 3));
        }).catch(() => {});
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return <div className="min-h-screen bg-ink pt-40"><div className="mx-auto h-[50vh] max-w-7xl animate-pulse rounded-2xl bg-white/5 md:mx-8 lg:mx-auto" /></div>;
  }

  if (status === "notfound") {
    return (
      <section className="flex min-h-[70vh] items-center bg-ink pt-20" data-testid="project-not-found">
        <div className="mx-auto max-w-xl px-5 text-center">
          <h1 className="font-display text-4xl text-offwhite">Project not found</h1>
          <p className="mt-4 text-sm text-white/60">{t("projects.empty")}</p>
          <Link to="/projects" data-testid="back-to-projects-link" className="mt-8 inline-block text-sm font-bold text-gold underline-offset-4 hover:underline">
            ← {t("nav.projects")}
          </Link>
        </div>
      </section>
    );
  }

  const p = project;
  return (
    <>
      <section className="relative flex min-h-[70vh] items-end overflow-hidden bg-ink pt-24" data-testid="project-hero">
        <img src={p.images?.[0]} alt={`${p.name} — ${p.category} in ${p.location}`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/40" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 md:px-8">
          <nav className="mb-6 flex items-center gap-1.5 text-xs font-semibold text-white/50" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-gold" data-testid="crumb-home">{t("nav.home")}</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/projects" className="hover:text-gold" data-testid="crumb-projects">{t("nav.projects")}</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-gold">{p.name}</span>
          </nav>
          <Reveal>
            <p className="eyebrow-dark">{p.location} · {p.category}</p>
            <h1 className="mt-3 max-w-3xl font-display text-5xl leading-[1.05] text-offwhite md:text-6xl">{p.name}</h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <h2 className="eyebrow mb-8">{t("caseStudy.overview")}</h2>
            <dl className="grid grid-cols-2 gap-8 md:grid-cols-4">
              <InfoRow label={t("caseStudy.location")} value={p.location} />
              <InfoRow label={t("caseStudy.type")} value={p.category} />
              <InfoRow label={t("caseStudy.floors")} value={p.floors} />
              <InfoRow label={t("caseStudy.status")} value={p.status} />
              <InfoRow label={t("caseStudy.year")} value={p.completion_year} />
              <InfoRow label={t("caseStudy.builtUp")} value={p.built_up_area} />
              <InfoRow label={t("caseStudy.plot")} value={p.plot_size} />
            </dl>
          </Reveal>
          {p.description && <Reveal><p className="mt-12 max-w-3xl text-base leading-relaxed text-charcoal/70">{p.description}</p></Reveal>}

          <div className="mt-16 grid gap-6 lg:grid-cols-3">
            {p.client_vision && (
              <Reveal><div className="h-full rounded-2xl bg-offwhite p-8"><h3 className="font-display text-xl text-charcoal">{t("caseStudy.vision")}</h3><p className="mt-3 text-sm leading-relaxed text-charcoal/65">{p.client_vision}</p></div></Reveal>
            )}
            {p.challenge && (
              <Reveal delay={0.08}><div className="h-full rounded-2xl bg-offwhite p-8"><h3 className="font-display text-xl text-charcoal">{t("caseStudy.challenge")}</h3><p className="mt-3 text-sm leading-relaxed text-charcoal/65">{p.challenge}</p></div></Reveal>
            )}
            {p.approach && (
              <Reveal delay={0.16}><div className="h-full rounded-2xl bg-charcoal p-8"><h3 className="font-display text-xl text-gold">{t("caseStudy.approach")}</h3><p className="mt-3 text-sm leading-relaxed text-white/70">{p.approach}</p></div></Reveal>
            )}
          </div>

          {p.images?.length > 1 && (
            <div className="mt-20">
              <Reveal><h2 className="eyebrow mb-8">{t("caseStudy.gallery")}</h2></Reveal>
              <div className="grid gap-5 sm:grid-cols-2">
                {p.images.map((src, i) => (
                  <Reveal key={src + i} delay={(i % 2) * 0.08}>
                    <img src={src} alt={`${p.name} — photo ${i + 1}`} loading="lazy"
                      data-testid={`gallery-image-${i}`}
                      className="aspect-[4/3] w-full rounded-2xl object-cover transition-transform duration-500 hover:scale-[1.02]" />
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {p.before_image && p.after_image && (
            <div className="mt-20">
              <Reveal>
                <h2 className="eyebrow mb-8">{t("caseStudy.beforeAfter")}</h2>
                <BeforeAfter before={p.before_image} after={p.after_image} />
              </Reveal>
            </div>
          )}
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-offwhite py-20 md:py-28" data-testid="related-projects">
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <Reveal><h2 className="eyebrow mb-10">{t("caseStudy.related")}</h2></Reveal>
            <div className="grid gap-8 md:grid-cols-3">
              {related.map((r, i) => <ProjectCard key={r.id} project={r} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <section className="bg-ink py-24 text-center" data-testid="project-cta">
        <Reveal>
          <p className="font-display text-3xl italic text-offwhite md:text-4xl">{t("caseStudy.cta")}</p>
          <ConsultButton testid="project-consult-button" className="mt-8" />
        </Reveal>
      </section>
      <FinalCta />
    </>
  );
}
